// ============================================
// RevenueCat (Apple IAP) wrapper for iOS.
// Every function no-ops on web and lazy-loads
// the native plugin only when `isNative()`.
//
// The RevenueCat webhook is the source of truth
// for entitlement - these client calls trigger the
// native purchase sheet and hand off to Apple; the
// webhook then writes `user_profiles.subscription_status`.
//
// Product IDs match what Lawrence sets up in App Store
// Connect and mirrors into RevenueCat.
// ============================================

import { isNative } from './native';

// Product IDs - must match App Store Connect + RevenueCat dashboard.
export const PRODUCT_ID_MONTHLY = 'prtcl_plus_monthly';
export const PRODUCT_ID_ANNUAL = 'prtcl_plus_annual';

// Entitlement ID configured in RevenueCat dashboard. Both products grant it.
export const ENTITLEMENT_ID = 'PRTCL Plus';

// Package identifiers from RevenueCat's default offering.
// `$rc_monthly` / `$rc_annual` are RevenueCat's standard slugs.
export const PACKAGE_MONTHLY = '$rc_monthly';
export const PACKAGE_ANNUAL = '$rc_annual';

export type PrtclPlan = 'monthly' | 'annual';

// Minimal shape we expose to the UI. Full PurchasesPackage carries a lot
// more than the UI needs; keep the surface small.
export interface DisplayablePackage {
  plan: PrtclPlan;
  priceString: string; // e.g. "$4.99"
  identifier: string;  // RevenueCat package identifier
  raw: unknown;        // The full PurchasesPackage for passing back to purchasePackage
}

export interface PurchaseOutcome {
  /** True if the active entitlement is granted after the purchase. */
  entitled: boolean;
  /** True if the user dismissed the Apple sheet. Not an error. */
  cancelled: boolean;
  /** Non-cancel error message, if any. */
  error?: string;
}

let configured = false;
let configurePromise: Promise<void> | null = null;

function getApiKey(): string | null {
  const key = import.meta.env.VITE_REVENUECAT_IOS_KEY;
  return typeof key === 'string' && key.length > 0 ? key : null;
}

/**
 * Initialize the RevenueCat SDK. Call once on app mount. Safe to call
 * multiple times - subsequent calls short-circuit.
 *
 * No-ops on web and if VITE_REVENUECAT_IOS_KEY is missing (so dev builds
 * without the key still run; gated features just behave as if the user
 * has no entitlement).
 */
export async function initPurchases(): Promise<void> {
  if (!isNative()) return;
  if (configured) return;
  if (configurePromise) return configurePromise;

  const apiKey = getApiKey();
  if (!apiKey) {
    console.warn('[revenuecat] VITE_REVENUECAT_IOS_KEY not set - skipping configure()');
    return;
  }

  configurePromise = (async () => {
    try {
      const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
      // Debug logs in dev, info in prod. RevenueCat's docs say this is the
      // single knob worth tuning; everything else has sensible defaults.
      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
      });
      await Purchases.configure({ apiKey });
      configured = true;
    } catch (err) {
      console.error('[revenuecat] configure failed:', err);
      configurePromise = null;
      throw err;
    }
  })();

  return configurePromise;
}

/**
 * Associate the RevenueCat App User ID with the Supabase user ID. Call
 * immediately after a successful sign-in. The webhook uses this ID to
 * locate the profile row to update.
 */
export async function loginToRevenueCat(userId: string): Promise<void> {
  if (!isNative()) return;
  if (!configured) {
    // configure() may not have finished yet on a cold start; wait for it.
    await initPurchases();
    if (!configured) return;
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logIn({ appUserID: userId });
  } catch (err) {
    console.error('[revenuecat] logIn failed:', err);
  }
}

/**
 * Detach the current user from RevenueCat. Call during sign-out so a
 * second user on the same device doesn't inherit the first user's
 * entitlement cache.
 */
export async function logoutFromRevenueCat(): Promise<void> {
  if (!isNative() || !configured) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logOut();
  } catch (err) {
    // logOut throws if the user is anonymous - non-fatal.
    console.warn('[revenuecat] logOut non-fatal:', err);
  }
}

/**
 * Fetch the two plans to show in the UI. Returns null on web, on error,
 * or if RevenueCat has no configured offering yet.
 */
export async function fetchPackages(): Promise<DisplayablePackage[] | null> {
  if (!isNative()) return null;
  if (!configured) {
    await initPurchases();
    if (!configured) return null;
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { current } = await Purchases.getOfferings();
    if (!current) return null;

    const out: DisplayablePackage[] = [];
    for (const pkg of current.availablePackages) {
      const plan = mapIdentifierToPlan(pkg.identifier);
      if (!plan) continue;
      out.push({
        plan,
        priceString: pkg.product.priceString,
        identifier: pkg.identifier,
        raw: pkg,
      });
    }
    // Stable order: monthly first, annual second. Matches the web flow.
    out.sort((a, b) => (a.plan === 'monthly' ? -1 : 1));
    return out;
  } catch (err) {
    console.error('[revenuecat] getOfferings failed:', err);
    return null;
  }
}

function mapIdentifierToPlan(identifier: string): PrtclPlan | null {
  if (identifier === PACKAGE_MONTHLY) return 'monthly';
  if (identifier === PACKAGE_ANNUAL) return 'annual';
  return null;
}

/**
 * Trigger the native Apple purchase sheet for the given package. The
 * returned PurchaseOutcome distinguishes user cancellation (not an error)
 * from real failures.
 */
export async function purchasePlan(pkg: DisplayablePackage): Promise<PurchaseOutcome> {
  if (!isNative() || !configured) {
    return { entitled: false, cancelled: false, error: 'not_configured' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const result = await Purchases.purchasePackage({ aPackage: pkg.raw as never });
    const activeEntitlements = result.customerInfo.entitlements.active;
    console.log('[revenuecat] purchase complete, active entitlements:', JSON.stringify(activeEntitlements));
    // Check our expected entitlement first; fall back to "any active
    // entitlement" so a naming mismatch in the RC dashboard doesn't
    // silently swallow a successful purchase.
    const entitled = Boolean(
      activeEntitlements[ENTITLEMENT_ID] || Object.keys(activeEntitlements).length > 0
    );
    return { entitled, cancelled: false };
  } catch (err) {
    const e = err as { userCancelled?: boolean; message?: string; code?: string };
    console.log('[revenuecat] purchasePackage caught error:', JSON.stringify({
      userCancelled: e?.userCancelled,
      code: e?.code,
      message: e?.message,
    }));
    if (e?.userCancelled || e?.code === '1') {
      return { entitled: false, cancelled: true };
    }
    console.error('[revenuecat] purchasePackage failed:', err);
    return { entitled: false, cancelled: false, error: e?.message ?? 'purchase_failed' };
  }
}

/**
 * Restore previous purchases. Apple requires this for any app that sells
 * subscriptions - users need a way to recover entitlement after reinstall
 * or on a new device.
 */
export async function restoreSubscription(): Promise<PurchaseOutcome> {
  if (!isNative() || !configured) {
    return { entitled: false, cancelled: false, error: 'not_configured' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.restorePurchases();
    const entitled = Boolean(customerInfo.entitlements.active[ENTITLEMENT_ID]);
    return { entitled, cancelled: false };
  } catch (err) {
    const e = err as { message?: string };
    console.error('[revenuecat] restorePurchases failed:', err);
    return { entitled: false, cancelled: false, error: e?.message ?? 'restore_failed' };
  }
}

/**
 * Open Apple's Manage Subscriptions sheet for the current user.
 *
 * The RevenueCat Capacitor plugin v13 does NOT expose a direct wrapper for
 * StoreKit's showManageSubscriptions, so we route through Safari View
 * Controller (via @capacitor/browser) to the canonical
 * apps.apple.com/account/subscriptions URL. iOS recognizes that URL and
 * surfaces the native subscription management sheet while keeping the user
 * in-app. User returns automatically when the sheet dismisses.
 *
 * On web, opens the same URL in a new tab.
 */
export async function showManageSubscriptions(): Promise<void> {
  const { openExternal } = await import('./native');
  await openExternal('https://apps.apple.com/account/subscriptions');
}
